import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import AppLink from 'components/App/AppLink';
import Button from 'components/App/Button';

import {
  PROMOTION_TYPE,
  PROMOTION_STATUS,
  PROMOTION_CATEGORY,
} from 'constants/promotion';
import { LOCATIONS } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import cn from 'utils/classnames';
import promotionRendering from 'utils/promotionRendering';
import { convertIsoDateToString } from 'utils';

import styles from './PromotionDataTable.module.scss';

const getBookingDateDisplay = (item) => {
  if (item.category === PROMOTION_CATEGORY.ACCOUNT) {
    return 'Luôn hoạt động';
  }

  if (item.category === PROMOTION_CATEGORY.LIMITED) {
    return `${convertIsoDateToString(item.bookingDate?.from)} - ${convertIsoDateToString(item.bookingDate?.to)}`;
  }

  if (item.bookingStartAt && item.bookingEndAt) {
    return `${convertIsoDateToString(item.bookingStartAt)} - ${convertIsoDateToString(item.bookingEndAt)}`;
  }

  return '-';
};

const getStayDateDisplay = (item) => {
  if (item.category === PROMOTION_CATEGORY.ACCOUNT) {
    return 'Luôn hoạt động';
  }

  if (item.category === PROMOTION_CATEGORY.LIMITED) {
    return `${convertIsoDateToString(item.checkInDate?.from)} - ${convertIsoDateToString(item.checkInDate?.to)}`;
  }

  if (item.stayAt) {
    return item.stayAt.map((stayAt) => (
      <p key={stayAt._id}>
        {`${convertIsoDateToString(stayAt.startAt)} - ${convertIsoDateToString(stayAt.endAt)}`}
      </p>
    ));
  }

  return '-';
};

function PromotionDataTable(props) {
  const { tab, promotions, onEndPromotion, onDeletePromotion } = props;
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const renderLink = React.useCallback((type) => {
    switch (type) {
      case PROMOTION_TYPE.BASIC:
        return LOCATIONS.LOCATION_DETAIL.CREATE_BASIC_PROMOTION;

      case PROMOTION_TYPE.BOOK_EARLY:
        return LOCATIONS.LOCATION_DETAIL.CREATE_EARLY_PROMOTION;

      case PROMOTION_TYPE.LAST_DAY:
        return LOCATIONS.LOCATION_DETAIL.CREATE_LAST_MINUTE_PROMOTION;

      case PROMOTION_TYPE.LONG_TERM:
        return LOCATIONS.LOCATION_DETAIL.CREATE_LONG_TERM_PROMOTION;

      case PROMOTION_TYPE.TOURIST:
        return LOCATIONS.LOCATION_DETAIL.CREATE_TOURIST_PROMOTION;

      case PROMOTION_TYPE.AGENCY:
        return LOCATIONS.LOCATION_DETAIL.CREATE_AGENCY_PROMOTION;

      case PROMOTION_TYPE.ENTERPRISE:
        return LOCATIONS.LOCATION_DETAIL.CREATE_ENTERPRISE_PROMOTION;

      case PROMOTION_TYPE.RANK:
        return LOCATIONS.LOCATION_DETAIL.CREATE_RANK_PROMOTION;

      case PROMOTION_TYPE.MARKETING:
        return LOCATIONS.LOCATION_DETAIL.CREATE_MARKETING_PROMOTION;

      default:
        return null;
    }
  }, []);

  return (
    <div className={styles.Component}>
      {promotions.length === 0 ? (
        <div className={styles.NoData}>Chưa có dữ liệu</div>
      ) : (
        <table className={styles.Table}>
          <colgroup>
            <col span="1" />
            <col span="1" />
            <col span="1" />
            <col span="1" />
            <col span="1" />
            <col span="1" />
            {/* <col span="1" />
          <col span="1" /> */}
          </colgroup>

          <thead className={styles.TableHeading}>
            <tr>
              <th>Tên khuyến mãi</th>
              <th>Giảm giá</th>
              <th>Ngày đặt</th>
              <th>Ngày lưu trú</th>
              <th>Tổng đặt phòng</th>
              <th>Đêm phòng</th>
              {/* <th>Giá bình quân</th>
            <th>Tổng doanh thu</th> */}
            </tr>
          </thead>

          <tbody className={styles.TableBody}>
            {promotions.map((item) => {
              const promotionType = item.discountType || item.type;
              const { icon: Icon, label } = promotionRendering(promotionType);
              const editPath = renderLink(promotionType);

              return (
                <tr key={item._id}>
                  <td>
                    <div className={styles.PromotionLabel}>
                      {Icon && <Icon width={24} height={24} />}

                      <div className={styles.PromotionName}>
                        <AppLink
                          href={{
                            pathname: editPath,
                            search: `?place=${placeId}&promotion=${item._id}`,
                          }}
                          className={styles.Link}
                        >
                          {item.name}
                        </AppLink>

                        <p>{label}</p>

                        <div className={styles.ButtonWrapper}>
                          {editPath &&
                            (([PROMOTION_STATUS.GOING].includes(tab) &&
                              item.category && item.type !== PROMOTION_TYPE.RANK) ||
                              [PROMOTION_STATUS.PENDING].includes(tab)) && (
                              <AppLink
                                href={{
                                  pathname: editPath,
                                  search: `?place=${placeId}&promotion=${item._id}`,
                                }}
                                className={styles.EditButton}
                              >
                                Chỉnh sửa
                              </AppLink>
                          )}

                          { tab === PROMOTION_STATUS.GOING &&
                          item.type !== PROMOTION_TYPE.MARKETING && (
                          <Button
                            isDefault
                            classes={cn(
                              styles.StopPromotionButton,
                            )}
                            onClick={onEndPromotion(item._id, item.category)}
                          >
                            Ngừng
                          </Button>
                          )
                        }

                          { tab === PROMOTION_STATUS.PENDING &&
                          item.type !== PROMOTION_TYPE.MARKETING && (
                          <Button
                            isDefault
                            classes={cn(
                              styles.StopPromotionButton,
                            )}
                            onClick={onDeletePromotion(item._id, item.category)}
                          >
                            Xoá
                          </Button>
                          )
                        }

                        </div>
                      </div>
                    </div>
                  </td>

                  <td>{`${item.discount}%`}</td>

                  <td>
                    <div className={styles.BookingDateWrap}>
                      <div className={styles.BookingDate}>
                        <p>{getBookingDateDisplay(item)}</p>
                      </div>

                      {item.bookingDescription && (
                        <p className={styles.BookingDescription}>
                          5+ ngày trước khi nhận phòng
                        </p>
                      )}
                    </div>
                  </td>

                  <td>
                    <div className={styles.StayDate}>
                      <div className={styles.Date}>
                        {getStayDateDisplay(item)}
                      </div>

                      {item.stayDescription && (
                        <p
                          className={cn(
                            styles.StayDescription,
                            item.stayDescriptionActive &&
                              styles.StayDescriptionActive,
                          )}
                        >
                          {item.stayDescription}
                        </p>
                      )}
                    </div>
                  </td>

                  <td>{item.totalBookings ? item.totalBookings : '-'}</td>

                  <td>{item.totalNights ? item.totalNights : '-'}</td>

                  {/* <td>
                  {item.averagePrice
                    ? currencyFormat(item.averagePrice, { isNotType: false })
                    : '-'}
                </td>

                <td>
                  {item.revenue
                    ? currencyFormat(item.revenue, { isNotType: false })
                    : '-'}
                </td> */}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

PromotionDataTable.propTypes = {
  tab: PropTypes.string.isRequired,
  promotions: PropTypes.instanceOf(Array),
  onEndPromotion: PropTypes.func,
  onDeletePromotion: PropTypes.func,
};

PromotionDataTable.defaultProps = {
  promotions: [],
  onEndPromotion: INITIAL_VALUE.FUNCTION,
  onDeletePromotion: INITIAL_VALUE.FUNCTION,
};

export default React.memo(PromotionDataTable);
